import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as ToggleIcon,
} from '@mui/icons-material';
import { LoanProduct } from '../../api/types/loan.types';
import { useLoanProducts, useDeleteLoanProduct, useUpdateLoanProduct } from '../../hooks/loans/useLoans';
import LoanProductDialog from './LoanProductDialog';
import { useSnackbar } from 'notistack';

const LoanProducts: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<LoanProduct | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuProduct, setMenuProduct] = useState<LoanProduct | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<LoanProduct | null>(null);

  const { data: products = [], isLoading, error } = useLoanProducts();
  const deleteMutation = useDeleteLoanProduct();
  const updateMutation = useUpdateLoanProduct();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: LoanProduct) => {
    setMenuAnchor(event.currentTarget);
    setMenuProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuProduct(null);
  };

  const handleAddProduct = () => {
    setSelectedProduct(null);
    setDialogOpen(true);
  };

  const handleEditProduct = () => {
    if (menuProduct) {
      setSelectedProduct(menuProduct);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    if (menuProduct) {
      setProductToDelete(menuProduct);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (productToDelete) {
      try {
        await deleteMutation.mutateAsync(productToDelete.productId);
        enqueueSnackbar('Loan product deleted successfully', { variant: 'success' });
        setDeleteDialogOpen(false);
        setProductToDelete(null);
      } catch (error) {
        console.error('Failed to delete product:', error);
        enqueueSnackbar(
          error instanceof Error ? error.message : 'Failed to delete loan product',
          { variant: 'error' }
        );
      }
    }
  };

  const handleToggleProduct = async () => {
    if (menuProduct) {
      const action = menuProduct.isActive ? 'disabled' : 'enabled';
      try {
        await updateMutation.mutateAsync({
          productId: menuProduct.productId,
          data: {
            productName: menuProduct.productName,
            productCode: menuProduct.productCode,
            description: menuProduct.description || undefined,
            minAmount: menuProduct.minAmount,
            maxAmount: menuProduct.maxAmount,
            interestRate1Week: menuProduct.interestRate1Week,
            interestRate2Weeks: menuProduct.interestRate2Weeks,
            interestRate1Month: menuProduct.interestRate1Month,
            processingFeePercentage: menuProduct.processingFeePercentage,
            latePaymentPenaltyPercentage: menuProduct.latePaymentPenaltyPercentage,
            firstTimeBorrowerLimit: menuProduct.firstTimeBorrowerLimit || '5000.00',
            limitIncreasePercentage: menuProduct.limitIncreasePercentage || '20.00',
            limitIncreaseAfterLoans: menuProduct.limitIncreaseAfterLoans || 3,
            isActive: !menuProduct.isActive,
          },
        });
        enqueueSnackbar(`Loan product ${action} successfully`, { variant: 'success' });
      } catch (error) {
        console.error('Failed to toggle product:', error);
        enqueueSnackbar(
          error instanceof Error ? error.message : `Failed to ${action.slice(0, -1)} loan product`,
          { variant: 'error' }
        );
      }
    }
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedProduct(null);
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const formatPercentage = (value: string) => {
    return `${parseFloat(value).toFixed(2)}%`;
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.productCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus =
      statusFilter === 'ALL' ||
      (statusFilter === 'ACTIVE' && product.isActive) ||
      (statusFilter === 'INACTIVE' && !product.isActive);
    return matchesSearch && matchesStatus;
  });

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Loan Products
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load loan products. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Loan Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage loan products, rates, and terms
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProduct}
          size="large"
        >
          Add Product
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search by name, code, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label="All"
            onClick={() => setStatusFilter('ALL')}
            color={statusFilter === 'ALL' ? 'primary' : 'default'}
            variant={statusFilter === 'ALL' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Active"
            onClick={() => setStatusFilter('ACTIVE')}
            color={statusFilter === 'ACTIVE' ? 'success' : 'default'}
            variant={statusFilter === 'ACTIVE' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Inactive"
            onClick={() => setStatusFilter('INACTIVE')}
            color={statusFilter === 'INACTIVE' ? 'error' : 'default'}
            variant={statusFilter === 'INACTIVE' ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Product Name</strong></TableCell>
              <TableCell><strong>Code</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Amount Range</strong></TableCell>
              <TableCell><strong>1 Week Rate</strong></TableCell>
              <TableCell><strong>2 Weeks Rate</strong></TableCell>
              <TableCell><strong>1 Month Rate</strong></TableCell>
              <TableCell><strong>Processing Fee</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery || statusFilter !== 'ALL'
                      ? 'No loan products match your filters'
                      : 'No loan products found. Click "Add Product" to create one.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow key={product.productId} hover>
                  <TableCell>
                    <Typography fontWeight="medium">{product.productName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={product.productCode} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                      {product.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(product.minAmount)} - {formatCurrency(product.maxAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatPercentage(product.interestRate1Week)}</TableCell>
                  <TableCell>{formatPercentage(product.interestRate2Weeks)}</TableCell>
                  <TableCell>{formatPercentage(product.interestRate1Month)}</TableCell>
                  <TableCell>{formatPercentage(product.processingFeePercentage)}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={product.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, product)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditProduct}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Product
        </MenuItem>
        <MenuItem onClick={handleToggleProduct}>
          <ToggleIcon sx={{ mr: 1 }} fontSize="small" />
          {menuProduct?.isActive ? 'Disable' : 'Enable'} Product
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Product
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Loan Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{productToDelete?.productName}"? This action will disable the product.
            {' '}Note that products currently in use by active loans cannot be deleted.
          </DialogContentText>
          {deleteMutation.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Failed to delete product'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Dialog */}
      <LoanProductDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        product={selectedProduct}
      />
    </Box>
  );
};

export default LoanProducts;
